import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import React, { useState, useEffect, useRef } from 'react';
import FilterNav from './FilterNav';
const homeUrl = process.env.REACT_APP_HOME_URL;
const apiUrl = process.env.REACT_APP_API_URL;
// Utility: Generate QuickChart.io chart URL
const getChartUrl = (data, label, clr = 'Data') => {
  if (!data || data.length === 0) return '';

  let chart_clr = '';
  if(clr > 0) {
  	chart_clr = 'green';
  }
  else {
  	chart_clr = 'red';
  }

  const chartData = {
    type: 'line',
    data: {
      labels: data.map((_, i) => i),
      datasets: [
        {
          label: label,
          data: data,
          borderColor: chart_clr,
          fill: false,
          borderWidth: 2,
          pointRadius: 0,
        },
      ],
    },
    options: {
      elements: { line: { tension: 0.4 } },
      plugins: { legend: { display: false } },
      scales: {
        x: { display: false },
        y: { display: false },
      },
    },
  };

  const encoded = encodeURIComponent(JSON.stringify(chartData));
  return `https://quickchart.io/chart?c=${encoded}`;
};

// Utility: Format numbers with commas
const formatNumber = (num) => {
  if (!num) return '-';
  return Intl.NumberFormat('en-US', { notation: 'compact' }).format(num);
};

const CACHE_DURATION = 60 * 1000 * 4; // 4 minute
const REFRESH_THRESHOLD = 60 * 1000; // 15 seconds

// Save to cache with expiry
const setCache = (key, data, ttl) => {
  const now = Date.now();
  const expiry = now + ttl;
  const item = { data, timestamp: now, expiry };
  localStorage.setItem(key, JSON.stringify(item));
};

// Load from cache and decide if it's fresh enough
const getCache = (key) => {
  const cached = localStorage.getItem(key);
  if (!cached) return null;

  const { data, timestamp, expiry } = JSON.parse(cached);
  const now = Date.now();

  if (now > expiry) {
    localStorage.removeItem(key);
    return null;
  }

  const isStale = now - timestamp > REFRESH_THRESHOLD;

  return { data, isStale };
};

const CoinTable = ({ coins, showBuyButton = true, showTimeframes, showMemeRank, activeTab }) => {
  const navigate = useNavigate();
  const [skeletoncard, setSkeletonCard] = useState(true);
  const [articles, setArticles] = useState([]);
  const [trendingCoins, setTrendingCoins] = useState([]);
  const [topGainers, setTopGainers] = useState([]);
  const [globalData, setGlobalData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [marketCap, setMarketCap] = useState(null);
  const [marketCapChange, setMarketCapChange] = useState(null);
  const [volume, setVolume] = useState(null);
  const [volumeChange, setVolumeChange] = useState(null);
  const [marketCapArray, setMarketCapArray] = useState([]);
  const [volumeArray, setVolumeArray] = useState([]);
  const scrollRef = useRef(null);

  const { pathname } = useLocation();

  useEffect(() => {
    const fetchRSS = async () => {
      try {
        const rssUrl = 'https://www.coindesk.com/arc/outboundfeeds/rss/';
        const proxyUrl = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(rssUrl)}`;

        const response = await fetch(proxyUrl);
        const data = await response.json();    

        setArticles(data.items);
        setSkeletonCard(false);
      } catch (err) {
        console.error('Error fetching RSS feed:', err);
      }
    };

    fetchRSS();
  }, []);
  
  const fetchMarketData = async () => {
    try {
      // 1. Global Market Cap & Volume Data
      const res = await fetch(`${apiUrl}/get-global-data`);
      const { data } = await res.json();

      setMarketCap(data.total_market_cap.usd);
      setMarketCapChange(data.market_cap_change_percentage_24h_usd);
      setVolume(data.total_volume.usd);

      // 2. Market Cap & Volume History (Bitcoin as reference)
      const chartRes = await fetch(`${apiUrl}/get-chartres-data`);
      const chartData = await chartRes.json();

      // Extract values
      const marketCaps = chartData.market_caps.map((entry) => entry[1]);
      const volumes = chartData.total_volumes.map((entry) => entry[1]);

      // Downsample to stay under QuickChart 288 point limit
      const downsample = (arr, step = 6) => arr.filter((_, i) => i % step === 0);

      setMarketCapArray(downsample(marketCaps));
      setVolumeArray(downsample(volumes));

      // Calculate % change for volume
      const volStart = volumes[0];
      const volEnd = volumes[volumes.length - 1];
      const volChange = ((volEnd - volStart) / volStart) * 100;
      setVolumeChange(volChange);

      setCache('MarketCapArray', downsample(marketCaps), CACHE_DURATION);
			setCache('VolumeArray', downsample(volumes), CACHE_DURATION);
			setCache('VolumeChange', volChange, CACHE_DURATION);

    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(false);
      
      // Fetch trending coins
      const trendingResponse = await fetch(`${apiUrl}/get-trendingcoin-data`);
      const trendingData = await trendingResponse.json();
      
      // Fetch top gainers
      const gainersResponse = await fetch(`${apiUrl}/get-topgainers-data`);
      const gainersData = await gainersResponse.json();
      
      // Fetch global market data
      const globalResponse = await fetch(`${apiUrl}/get-globalcoin-data`);
      const global = await globalResponse.json();

      setTrendingCoins(trendingData.coins.slice(0, 5));
      setTopGainers(gainersData);
      setGlobalData(global.data);

      setCache('TrendingCoins', trendingData.coins.slice(0, 5), CACHE_DURATION);
      setCache('TopGainers', gainersData, CACHE_DURATION);
      setCache('GlobalData', global.data, CACHE_DURATION);
      scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {

    const cached = getCache('MarketCapArray');

    if (cached && !cached.isStale) {
      setMarketCapArray(getCache('MarketCapArray').data);
      setVolumeArray(getCache('VolumeArray').data);
      setVolumeChange(getCache('VolumeChange').data);
      setTrendingCoins(getCache('TrendingCoins').data);
      setTopGainers(getCache('TopGainers').data);
      setGlobalData(getCache('GlobalData').data);
      setTimeout(() => {
	      scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
	    }, 1000);
      
    } 
    else {
      fetchMarketData();
      fetchData();
    }

  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

if (coins.length < 1) return (
		<>
			<div className="skeleton-card">
				<div className="crypto_price_table">
					<div className="container">
						<div className="table-responsive">
							<table className="table">
								<thead>
									<tr>
										<th scope="col"></th>
										<th scope="col"><div className="skeleton skeleton-title"></div></th>
										<th scope="col"><div className="skeleton skeleton-title"></div></th>
										<th scope="col"><div className="skeleton skeleton-title"></div></th>
										<th scope="col"><div className="skeleton skeleton-title"></div></th>
										<th scope="col"><div className="skeleton skeleton-title"></div></th>
										<th scope="col"><div className="skeleton skeleton-title"></div></th>
										<th scope="col"><div className="skeleton skeleton-title"></div></th>
										<th scope="col"><div className="skeleton skeleton-title"></div></th>
										<th scope="col"><div className="skeleton skeleton-title"></div></th>
									</tr>
								</thead>
								
								<tbody>
									<tr>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
									</tr>
									
									<tr>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
									</tr>
									
									<tr>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
									</tr>
									
									<tr>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
									</tr>
									
									<tr>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
									</tr>
									
									<tr>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
									</tr>
									
									<tr>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
									</tr>
									
									<tr>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
									</tr>
									
									<tr>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
									</tr>
									
									<tr>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>
		</>
);

  return (
    <div className="home_wrapper">

      <div className="crypto_price_table">
        <div className="container">
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Coin</th>
                  <th scope="col">Price</th>
                  <th scope="col">24h</th>
                  <th scope="col">24h Volume</th>
                  <th scope="col">Market Cap</th>
                  {showMemeRank && (
                    <th className="py-3 px-4 text-left">Meme Rank</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {coins.length > 0 && coins.map((coin) => (
                  <tr key={coin.id} >
                    <td>
                      <div className="cryptonamebtn">
                        <div className="cryptoname">
                          <img alt="{coin.name} Logo" src={coin.image} />
                          <span className="coinname" style={{ cursor: "pointer" }} onClick={() => navigate(`/coin/${coin.id}`)}>{coin.name} <span className="cointxt">{coin.symbol}</span></span>
                        </div>
                      </div>
                    </td>

                    <td >
                    {(pathname === '/trending-coins' || activeTab === 'trending')
                			? `$${coin.price?.toLocaleString()}`
                			: `$${coin.current_price?.toLocaleString()}`}
                    </td>
                    
                    <td className={`updown_txt ${
                      coin.price_change_percentage_24h >= 0 
                        ? 'upcoin' 
                        : 'downcoin'
                    }`}>
                      {coin.price_change_percentage_24h?.toFixed(2)}%
                    </td>
                    
                    
                    <td >
	                    {(pathname === '/trending-coins' || activeTab === 'trending')
	                			? `${coin.total_volume?.toLocaleString()}`
	                			: `$${coin.current_price?.toLocaleString()}`}
                    </td>
                    
                    <td >
	                    {(pathname === '/trending-coins' || activeTab === 'trending')
	                			? `${coin.market_cap?.toLocaleString()}`
	                			: `$${coin.current_price?.toLocaleString()}`}
                    </td>
                    {showMemeRank && (
                      <td >
                        #{coin.meme_rank}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div> 
    </div>     
  );
};

export default CoinTable;